import { useEffect, useState } from "react";
import { Quote, MapPin, Sparkles } from "lucide-react";
import { PRICING_TESTIMONIALS as FALLBACK } from "../data/pricing_testimonials";
import { api } from "../lib/api";

/**
 * Micro-testimonial strip that sits directly under the /pricing tier cards.
 * Fetches live testimonials from /api/testimonials (managed at /admin/testimonials).
 * Falls back to the static placeholder set in /data/pricing_testimonials.js if the API
 * returns nothing (empty DB) so the layout never breaks.
 */
export function PricingTestimonialStrip() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/testimonials")
      .then((r) => {
        if (cancelled) return;
        const live = Array.isArray(r.data) ? r.data : [];
        setItems(live.length > 0 ? live : FALLBACK);
      })
      .catch(() => {
        if (!cancelled) setItems(FALLBACK);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (items === null) return null; // don't render until we know
  if (!items.length) return null;

  return (
    <div className="mt-16 md:mt-20" data-testid="pricing-testimonial-strip">
      <div className="text-center mb-8">
        <div className="text-purple-400 text-xs tracking-[0.35em] uppercase font-mono mb-3">
          · Early voices from the vault ·
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight text-white">
          What the first founders are saying.
        </h2>
        <p className="mt-3 text-zinc-500 text-sm">
          Real feedback from early Lifetime Access members. Yours could be next.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 max-w-4xl mx-auto">
        {items.slice(0, 2).map((t, i) => {
          const initials = t.attribution
            .split(" ")
            .map((w) => w[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();
          return (
            <figure
              key={t.id || i}
              className="relative bg-[#0a0710] border border-purple-500/20 hover:border-purple-500/50 transition-colors duration-500 p-6 md:p-7 flex flex-col"
              data-testid={`pricing-testimonial-card-${i}`}
            >
              <Quote
                size={22}
                className="text-purple-500/40 mb-4 shrink-0"
                strokeWidth={2.5}
              />
              {t.revenue && (
                <div className="inline-flex self-start items-center gap-1.5 px-2 py-1 mb-4 bg-amber-500/[0.08] border border-amber-400/30 text-amber-300 text-[10px] tracking-[0.2em] uppercase font-bold">
                  {t.revenue}
                </div>
              )}
              <blockquote className="text-zinc-200 text-[15px] leading-relaxed mb-6 flex-1">
                &ldquo;{t.quote}&rdquo;
              </blockquote>
              <figcaption className="flex items-center gap-3 pt-4 border-t border-zinc-900">
                <div
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-black text-xs font-bold shadow-[0_0_12px_rgba(168,85,247,0.35)]"
                  aria-hidden="true"
                >
                  {initials}
                </div>
                <div className="min-w-0">
                  <div className="text-white text-sm font-semibold truncate">
                    {t.attribution}
                    {t.verified && (
                      <span
                        className="inline-flex items-center gap-1 ml-2 text-purple-300 text-[10px] tracking-wider uppercase font-mono"
                        title="Quote verified from source"
                      >
                        <Sparkles size={9} />
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-zinc-500 text-xs mt-0.5">
                    <MapPin size={11} className="shrink-0" />
                    <span className="truncate">{t.location}</span>
                    <span className="text-purple-400/60">·</span>
                    <span className="text-purple-300/80 uppercase tracking-wider text-[10px]">
                      {t.tier}
                    </span>
                  </div>
                </div>
              </figcaption>
            </figure>
          );
        })}
      </div>

      <p className="mt-8 text-center text-zinc-600 text-xs tracking-wide">
        Want your quote here? Buy lifetime, use the vault for 7 days, then reply to the welcome email.
      </p>
    </div>
  );
}
